import {
  ArrowArcLeft,
  BookOpenText,
  DotsThreeVertical,
  Gauge,
  NotePencil,
  Pencil,
  Plus,
  StopCircle,
  TextItalic,
  TrendUp,
} from '@phosphor-icons/react';
import {array, func} from 'prop-types';
import {Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {useParams} from 'react-router-dom';

import {STATUSES, STATUSES_FINISHED} from '../../../../lib/training.mjs';
import {useMesocycle} from '../api.js';
import {useConfirmation} from '../ConfirmationContext.jsx';
import {DIALOG_CONFIGS} from '../constants.js';
import {useBreakpoint} from '../utils/hooks.js';
import useLocalStore from '../utils/useLocalStore.js';
import ExerciseSelector from './ExerciseSelector.jsx';
import MesoDayNote from './MesoDayNote.jsx';
import MesoNotesDialog from './MesoNotesDialog.jsx';
import Navigation from './Navigation.jsx';
import CompleteBadge from './ui/CompleteBadge.jsx';
import Expander from './ui/Expander.jsx';
import IconButton from './ui/IconButton.jsx';
import IconButtonMenuLabel from './ui/IconButtonMenuLabel.jsx';
import {MenuButton} from './ui/MenuItem.jsx';
import UpdateMesoDayLabelDialog from './UpdateMesoDayLabelDialog.jsx';

export default function MesoDayHeader({bodyweightDexes, setProgressionsOpen}) {
  const {mesoKey, week, day: dayParam} = useParams();
  const {data: meso} = useMesocycle(mesoKey);
  const {confirmation} = useConfirmation();

  const progressRef = useRef();
  const isMobile = !useBreakpoint('desktop');

  const day = meso.weeks[week - 1].days[dayParam - 1];
  const isComplete = !!meso.finishedAt;

  const setEndMesoOpen = useLocalStore(st => st.setEndMesoOpen);
  const setIsMesoSummaryOpen = useLocalStore(st => st.setIsMesoSummaryOpen);
  const setReadynessDialogOpen = useLocalStore(st => st.setReadynessDialogOpen);
  const setResetDayOpen = useLocalStore(st => st.setResetDayOpen);
  const setSkipMesoDayOpen = useLocalStore(st => st.setSkipMesoDayOpen);

  const [addExerciseOpen, setAddExerciseOpen] = useState(false);
  const [mesoNotesOpen, setMesoNotesOpen] = useState(false);
  const [progressContainerWidth, setProgressContainerWidth] = useState(0);
  const [renameDayOpen, setRenameDayOpen] = useState(false);
  const [shouldAddNote, setShouldAddNote] = useState(false);

  const canReset = useMemo(() => {
    return !day.finishedAt && day.exercises.find(dex => dex?.sets.find(set => set.finishedAt));
  }, [day.exercises, day.finishedAt]);

  const handleAddExerciseClose = useCallback(() => {
    setAddExerciseOpen(false);
  }, []);

  const handleResize = useCallback(() => {
    if (progressRef.current) {
      const {width} = progressRef.current.getBoundingClientRect();
      setProgressContainerWidth(width);
    }
  }, []);

  useLayoutEffect(handleResize, [handleResize]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  const progressWidth = useMemo(() => {
    const sets = day.exercises.flatMap(dex => dex.sets);

    if (sets.length) {
      const completeSets = sets.filter(set => set.finishedAt);

      return Math.round(progressContainerWidth * (completeSets.length / sets.length));
    }

    return 0;
  }, [day.exercises, progressContainerWidth]);

  return (
    <Fragment>
      <div
        className={`bg-base-100 pt-3 dark:bg-base-200 dark:shadow-2xl ${
          isMobile ? 'border-b shadow-sm dark:border-base-100' : 'shadow'
        }`}
      >
        <h2 className="w-5/6 truncate px-4 text-xs uppercase text-base-content/60">{meso.name}</h2>

        <div className="flex items-center justify-between px-4 pb-2 pt-0.5">
          <h2 className="flex items-baseline space-x-1 align-top font-semibold text-base-content">
            <span className="text-sm uppercase md:text-base">Week</span>
            <span className="text-lg desktop:text-xl">{week} </span>
            <span className="text-sm uppercase md:text-base"> Day</span>
            <span className="text-lg md:text-xl">{dayParam} </span>
            <span>{day.label}</span>
          </h2>

          <div className="flex items-center gap-4">
            {day.finishedAt && <CompleteBadge finishedAt={day.finishedAt} />}

            <Navigation meso={meso} />

            <IconButton icon={<DotsThreeVertical weight="bold" />}>
              <IconButtonMenuLabel label="mesocycle" />

              {!isComplete && (
                <MenuButton
                  onClick={() => setEndMesoOpen(true)}
                  label="End meso"
                  icon={<StopCircle size={16} />}
                />
              )}

              <MenuButton
                onClick={() => setMesoNotesOpen(true)}
                label="View notes"
                icon={<NotePencil size={16} />}
              />

              <MenuButton
                onClick={() => setProgressionsOpen(true)}
                label="Edit set progressions"
                icon={<TrendUp size={16} />}
              />

              <MenuButton
                onClick={() => setIsMesoSummaryOpen(true)}
                label="Mesocycle summary"
                icon={<BookOpenText size={16} />}
              />

              <IconButtonMenuLabel label="workout" />

              <MenuButton
                onClick={() => setShouldAddNote(true)}
                label="New workout note"
                icon={<Pencil size={16} />}
              />
              <MenuButton
                onClick={() => setRenameDayOpen(true)}
                label="Rename"
                icon={<TextItalic size={16} />}
              />

              {!day.finishedAt && (
                <MenuButton
                  onClick={() => setAddExerciseOpen(true)}
                  label="Add exercise"
                  icon={<Plus size={16} />}
                />
              )}

              {bodyweightDexes.length > 0 && (
                <MenuButton
                  onClick={async () => {
                    if (STATUSES_FINISHED.day.includes(day.status)) {
                      const shouldContinue = await confirmation(DIALOG_CONFIGS.dayCompleteWarning);
                      if (!shouldContinue) {
                        return;
                      }
                    }
                    setReadynessDialogOpen(true);
                  }}
                  label="Set bodyweight"
                  icon={<Gauge size={16} />}
                />
              )}

              {canReset && (
                <MenuButton
                  onClick={() => setResetDayOpen(true)}
                  label="Reset"
                  icon={<ArrowArcLeft size={16} />}
                />
              )}

              {!day.finishedAt &&
                day.status !== STATUSES.day.pending &&
                day.status !== STATUSES.day.pendingConfirmation && (
                  <MenuButton
                    onClick={() => setSkipMesoDayOpen(true)}
                    label="End workout"
                    icon={<StopCircle size={16} />}
                  />
                )}
            </IconButton>
          </div>
        </div>

        <div className="px-4">
          <Expander isOpen={!day.notes.length} specificHeight={8} />
          <Expander isOpen={!!day.notes.length} specificHeight={8} />

          <div className="divide-y divide-base-200 dark:divide-base-100">
            {day.notes.map(dayNote => (
              <MesoDayNote key={`day-note-${dayNote.id}`} day={day} dayNote={dayNote} />
            ))}
          </div>

          <MesoDayNote
            // This is for a new note
            day={day}
            isNewNoteOpen={shouldAddNote}
            onNewNoteClose={() => setShouldAddNote(false)}
          />

          <Expander isOpen={!!day.notes.length} specificHeight={16} />
        </div>

        <div className="w-full bg-gray-200 dark:bg-base-100" ref={progressRef}>
          <div
            className="bg-emerald-500 transition-width duration-500 ease-in-out dark:bg-emerald-700"
            style={{width: progressWidth, height: 2}}
          />
        </div>
      </div>

      <ExerciseSelector isOpen={addExerciseOpen} onClose={handleAddExerciseClose} day={day} />

      <UpdateMesoDayLabelDialog
        isOpen={renameDayOpen}
        onClose={() => setRenameDayOpen(false)}
        day={day}
      />

      <MesoNotesDialog
        isOpen={mesoNotesOpen}
        notes={meso.notes}
        onClose={() => setMesoNotesOpen(false)}
      />
    </Fragment>
  );
}

MesoDayHeader.propTypes = {
  bodyweightDexes: array.isRequired,
  setProgressionsOpen: func.isRequired,
};
